import { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalHeader } from "@heroui/react";
import { ContractMilestoneDate, MilestoneDateType, MilestoneOrigin, RentalContract, ShowMilestoneInOverview } from 'shared/types/rental-contracts';
import { EditableSelectComponent, EditableTextareaComponent } from 'components/index';
import ButtonNew from 'components/buttonNew/buttonNew';
import { Clock, Info, Question } from '@phosphor-icons/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession, useUser } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { Calendar, PlusCircle } from 'assets/icons';
import ReactDatePicker from 'react-datepicker';

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;

interface AddMilestoneDateModalProps {
    isOpen: boolean;
    onClose: () => void;
    contract: RentalContract;
    addMilestoneDateToContract: (milestoneDate: ContractMilestoneDate) => void;
}

const AddMilestoneDateModal = ({ isOpen, onClose, contract, addMilestoneDateToContract }: AddMilestoneDateModalProps) => {
    const [milestoneDate, setMilestoneDate] = useState<ContractMilestoneDate>();
    const { session } = useSession();
    const { user } = useUser();

    useEffect(() => {
        setMilestoneDate((prev: any) => ({
            ...prev,
            rentalContractId: contract?.id,
            userId: user?.id,
            date: null,
            milestoneDateType: null,
            origin: MilestoneOrigin.USER,
            customDescription: null,
            showMilestoneInOverview: ShowMilestoneInOverview.YES,
            milestoneArchivedFromOverview: false,
            milestoneArchivedFromOverviewByUserId: null
        }));
    }, [contract, user]);

    const handleAddGuarantee = async () => {
        if (!milestoneDate?.milestoneDateType || !milestoneDate?.date) {
            toast.error("Gelieve een datum en gebeurtenis type te selecteren");
            return;
        }

        if (!milestoneDate?.rentalContractId) {
            toast.error("Het achterliggende contract is niet meer gelinkt. Je moet je modal even sluiten en opnieuw openen.");
            return;
        }

        try {
            let res = await fetchWithAuth(
                `${API_URL}/rental/add-contract-milestone-date`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ milestoneDate: milestoneDate }),
                },
                session,
            );

            const savedMilestoneDate = await res.json();

            if (res.ok) {
                addMilestoneDateToContract(savedMilestoneDate);
                toast.success("Gebeurtenis werd aan contract toegevoegd!");
                handleClose();
            } else {
                toast.error("Er is iets misgegaan bij het toevoegen van de belangrijke gebeurtenis");
            }
        } catch (error) {
            toast.error("Er is iets misgegaan bij het toevoegen van de belangrijke gebeurtenis");
        }
    }

    const milestoneDateTypeOptions = Object.values(MilestoneDateType).map((value) => ({
        value: value,
        label: value,
    }));

    const milestoneInOverviewOptions = Object.values(ShowMilestoneInOverview).map((value) => ({
        value: value,
        label: value,
    }));

    const handleClose = () => {
        setMilestoneDate({ date: null, milestoneDateType: null, rentalContract: contract, origin: null, customDescription: null, userId: "", showMilestoneInOverview: ShowMilestoneInOverview.YES, milestoneArchivedFromOverview: false, milestoneArchivedFromOverviewByUserId: null });
        onClose();
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                size="4xl"
                shouldBlockScroll={true}
                scrollBehavior="outside"
                backdrop="blur"
                className="p-8 "
            >
                <ModalContent className="flex flex-col gap-4 font-normal text-neutral-primary">
                    <ModalHeader className="flex flex-row items-center justify-between p-0 font-normal">
                        <p>Voeg nieuwe gebeurtenis toe</p>
                        <ButtonNew
                            icon={<PlusCircle />}
                            label="Voeg nieuwe gebeurtenis toe"
                            onClick={handleAddGuarantee}
                            variant="primaryMoss"
                        />
                    </ModalHeader>
                    <ModalBody className="grid gap-5 gap-y-8 px-0 sm:grid-cols-2">
                        {milestoneDate && <>

                            <div className="flex flex-col gap-2.5">
                                <header className="flex items-center gap-1.5 text-sm font-medium">
                                    <Calendar className="h-[18px] w-[18px]" />
                                    Datum van gebeurtenis
                                </header>
                                <DatePicker selected={milestoneDate.date} onChange={(date: Date) => setMilestoneDate((prev: any) => ({ ...prev, date: date }))} dateFormat="dd/MM/yyyy" placeholderText='Selecteer een datum'
                                    className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50" />
                            </div>
                            <div className="flex flex-col gap-2.5">
                                <header className="flex items-center gap-1.5 text-sm font-medium">
                                    <Question className="h-[18px] w-[18px]" />
                                    Type gebeurtenis
                                </header>
                                <EditableSelectComponent
                                    label="Type gebeurtenis"
                                    editMode={true}
                                    value={
                                        milestoneDate.milestoneDateType as string
                                    }
                                    options={milestoneDateTypeOptions}
                                    onChange={(value: string) =>
                                        setMilestoneDate((prev: any) => ({
                                            ...prev,
                                            milestoneDateType: value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="flex flex-col gap-2.5">
                                <header className="flex items-center gap-1.5 text-sm font-medium">
                                    <Clock className="h-[18px] w-[18px]" />
                                    Moet deze gebeurtenis in het overzicht verschijnen?
                                </header>
                                <EditableSelectComponent
                                    label="Gebeurtenis in overzicht tonen?"
                                    editMode={true}
                                    value={
                                        milestoneDate.showMilestoneInOverview as string
                                    }
                                    options={milestoneInOverviewOptions}
                                    onChange={(value: string) =>
                                        setMilestoneDate((prev: any) => ({
                                            ...prev,
                                            showMilestoneInOverview: value,
                                        }))
                                    }
                                />
                            </div>
                            {milestoneDate && milestoneDate.milestoneDateType == MilestoneDateType.CUSTOM && <div className="flex flex-col gap-2.5">
                                <header className="flex items-center gap-1.5 text-sm font-medium">
                                    <Info className="h-[18px] w-[18px]" />
                                    Beschrijving van de gebeurtenis
                                </header>
                                <EditableTextareaComponent
                                    label='Beschrijving van de gebeurtenis'
                                    editMode={true}
                                    isLoading={false}
                                    value={milestoneDate.customDescription as string}
                                    onChange={(newValue) =>
                                        setMilestoneDate((prev: any) => ({
                                            ...prev,
                                            customDescription: newValue,
                                        }))
                                    }
                                />
                            </div>}
                        </>}
                    </ModalBody>

                </ModalContent>
            </Modal>
        </>
    );
};

export default AddMilestoneDateModal;
